import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, ApiError, useCurrentCompany, useMe, useRefreshMe, useSelectCompany } from '../../lib'
import { Empty, ErrorBox, Loading, NoCompany } from '../../app/ui'
import { PageBand } from '../../app/PageBand'
import type { ClaimPage as Claim, ClaimRow, ConsumeResult, InviteInfo } from './types'

const STATUS: Record<ClaimRow['status'], string> = {
  sent: 'Pick a company',
  needs_admin: 'Waiting for an admin',
  linked: 'Earning points',
  rejected: 'Not yours',
  expired: 'Link expired',
  review: "We're checking",
}

function SignInFirst({ next, what }: { next: string; what: string }) {
  return (
    <div className="page narrow stack" data-testid="accounts-sign-in-first">
      <h1>Sign in to {what}</h1>
      <Link className="button primary" to={`/login?next=${encodeURIComponent(next)}`} data-testid="accounts-sign-in-first-button">
        Sign in
      </Link>
    </div>
  )
}

/** A link that is wrong, old or used: say so plainly and give a way on, not only an error line. */
function LinkError({ error }: { error: unknown }) {
  if (!(error instanceof ApiError && error.status === 404)) return <ErrorBox error={error} />
  return (
    <div className="page narrow stack" data-testid="accounts-dead-link">
      <h1>This link doesn't work</h1>
      <p>It may be old or already used. Ask whoever sent it for a new one.</p>
      <Link className="button primary" to="/">Go to ernd</Link>
    </div>
  )
}

const INVITE_STATE: Record<string, string> = {
  accepted: 'This invite was already used.',
  revoked: 'This invite was cancelled. Ask a teammate to send a new one.',
  expired: 'This invite has expired. Ask a teammate to send a new one.',
}

export function ClaimTokenPage() {
  const { token = '' } = useParams()
  const { me, isLoading } = useMe()
  const refresh = useRefreshMe()
  const select = useSelectCompany()
  const navigate = useNavigate()
  const q = useQuery({
    queryKey: ['accounts', 'claim', token],
    queryFn: () => api<Claim>('GET', `/api/claims/${encodeURIComponent(token)}`),
    enabled: !!me?.user,
    retry: false,
  })
  const [pick, setPick] = useState<string>('')
  const [newName, setNewName] = useState('')
  const consume = useMutation({
    mutationFn: (body: object) => api<ConsumeResult>('POST', `/api/claims/${encodeURIComponent(token)}/consume`, body),
    onSuccess: async (r) => {
      select(r.company_id)
      await refresh()
      q.refetch()
    },
  })

  if (isLoading) return <Loading />
  if (!me?.user) return <SignInFirst next={`/claim/${token}`} what="start earning" />
  if (q.isLoading) return <Loading />
  if (q.error || !q.data) return <LinkError error={q.error} />
  const c = q.data
  const done = consume.data
  const status = done?.status ?? c.status
  const companies = c.companies
  const chosen = pick || companies[0]?.id || 'new'
  const chosenName = chosen === 'new' ? newName || 'a new company' : companies.find((x) => x.id === chosen)?.name
  const account = c.account.name ?? c.account.external_customer_id

  if (status !== 'sent') {
    return (
      <div className="page narrow stack" data-testid="accounts-claim">
        <h1>Your {c.partner.name} spend</h1>
        <p data-testid="accounts-claim-status" className={`status-${status}`}>
          {status === 'linked' ? `Earning points${c.target_company ? ` for ${c.target_company.name}` : ''}.` : null}
          {status === 'needs_admin' ? 'Waiting for an admin of your company to say yes.' : null}
          {status === 'review' ? "Another company has this account. We'll check and email you." : null}
          {status === 'rejected' ? 'This account is not linked to you.' : null}
          {status === 'expired' ? 'This link has expired.' : null}
        </p>
        <button className="button" onClick={() => navigate('/')} data-testid="accounts-claim-to-app">
          Go to overview
        </button>
      </div>
    )
  }
  if (c.kind === 'admin') {
    const company = c.target_company
    return (
      <div className="page narrow stack" data-testid="accounts-claim">
        <h1>{c.partner.name} bills {c.contact_email}</h1>
        {c.email_matches && company ? (
          <>
            <p data-testid="accounts-claim-copy">
              Is this <strong>{company.name}</strong>? If you say yes, what you pay {c.partner.name} earns points.
            </p>
            <button
              className="button primary"
              disabled={consume.isPending}
              data-testid="accounts-claim-yes"
              onClick={() => consume.mutate({ company_id: company.id })}
            >
              Yes, this is us
            </button>
            <p className="muted">If not, close this page.</p>
          </>
        ) : (
          <p>Only an admin of {company?.name ?? 'the company we asked'} can say yes.</p>
        )}
        {consume.error ? <ErrorBox error={consume.error} /> : null}
      </div>
    )
  }
  if (!c.email_matches) {
    return (
      <div className="page narrow stack" data-testid="accounts-claim">
        <h1>Wrong email</h1>
        <p>
          This link is for <strong>{c.contact_email}</strong>. Sign in with that email.
        </p>
      </div>
    )
  }

  return (
    <div className="page narrow stack" data-testid="accounts-claim">
      <h1>Earn points on your {c.partner.name} spend</h1>
      <p data-testid="accounts-claim-copy">
        {c.partner.name} bills you as <strong>{account}</strong>. Add it to <strong>{chosenName}</strong>?
      </p>
      <label>
        Company
        <select value={chosen} onChange={(e) => setPick(e.target.value)} data-testid="accounts-claim-company">
          {companies.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
              {x.role === 'member' ? ' (an admin says yes)' : ''}
            </option>
          ))}
          <option value="new">New company…</option>
        </select>
      </label>
      {chosen === 'new' ? (
        <label>
          New company name
          <input value={newName} onChange={(e) => setNewName(e.target.value)} data-testid="accounts-claim-new-name" />
        </label>
      ) : null}
      <button
        className="button primary"
        disabled={consume.isPending || (chosen === 'new' && !newName.trim())}
        data-testid="accounts-claim-attach"
        onClick={() => consume.mutate(chosen === 'new' ? { new_company: { name: newName.trim() } } : { company_id: chosen })}
      >
        Add
      </button>
      {consume.error ? <ErrorBox error={consume.error} /> : null}
    </div>
  )
}

export function ClaimsPage() {
  const company = useCurrentCompany()
  const qc = useQueryClient()
  const key = ['accounts', 'claims', company?.id]
  const q = useQuery({
    queryKey: key,
    queryFn: () => api<{ rows: ClaimRow[]; my_role: string }>('GET', `/api/companies/${company!.id}/claims`),
    enabled: !!company,
  })
  const approve = useMutation({
    mutationFn: (id: string) => api('POST', `/api/claims/${id}/approve`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  })
  if (!company) return <NoCompany />
  if (q.isLoading || !q.data) return q.error ? <ErrorBox error={q.error} /> : <Loading />
  const admin = q.data.my_role === 'admin'

  return (
    <div className="stack" data-testid="accounts-claims">
      <PageBand title="Partner accounts" sub="Spend at a partner links here when you sign in with the email it bills." />
      {approve.error ? <ErrorBox error={approve.error} /> : null}
      {q.data.rows.length === 0 ? (
        <Empty>Nothing yet.</Empty>
      ) : (
        <div className="e-panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Partner</th>
                <th>Billed as</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.rows.map((r) => (
                <tr key={r.id} data-testid="accounts-claim-row">
                  <td>{r.partner_name}</td>
                  <td>{r.account_name}</td>
                  <td data-testid="accounts-claim-row-status">
                    {STATUS[r.status]}
                    {r.status === 'sent' ? <span className="muted"> (email sent to {r.contact_email})</span> : null}
                    {r.status === 'needs_admin' && r.claimant_email ? <span className="muted"> (from {r.claimant_email})</span> : null}
                  </td>
                  <td>
                    {admin && r.status === 'needs_admin' ? (
                      <button className="button primary" onClick={() => approve.mutate(r.id)} data-testid="accounts-claim-approve">
                        Say yes
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export function InvitePage() {
  const { token = '' } = useParams()
  const { me, isLoading } = useMe()
  const refresh = useRefreshMe()
  const select = useSelectCompany()
  const navigate = useNavigate()
  const q = useQuery({
    queryKey: ['accounts', 'invite', token],
    queryFn: () => api<InviteInfo>('GET', `/api/invites/${encodeURIComponent(token)}`),
    enabled: !!me?.user,
    retry: false,
  })
  const accept = useMutation({
    mutationFn: () => api<{ company_id: string }>('POST', `/api/invites/${encodeURIComponent(token)}/accept`, {}),
    onSuccess: async (r) => {
      select(r.company_id)
      await refresh()
      navigate('/')
    },
  })
  if (isLoading) return <Loading />
  if (!me?.user) return <SignInFirst next={`/invite/${token}`} what="join your team" />
  if (q.isLoading) return <Loading />
  if (q.error || !q.data) return <LinkError error={q.error} />
  const i = q.data
  return (
    <div className="page narrow stack" data-testid="accounts-invite">
      <h1>Join {i.company.name}</h1>
      {i.state !== 'open' ? (
        <>
          <p data-testid="accounts-invite-state">{INVITE_STATE[i.state] ?? "This invite can't be used."}</p>
          <Link className="button" to="/">Go to ernd</Link>
        </>
      ) : !i.email_matches ? (
        <p>
          This invite is for <strong>{i.email}</strong>. Sign in with that email.
        </p>
      ) : (
        <>
          <p>
            You're invited as {i.role === 'admin' ? 'an admin' : 'a member'}.
          </p>
          <button className="button primary" disabled={accept.isPending} onClick={() => accept.mutate()} data-testid="accounts-invite-accept">
            Join
          </button>
        </>
      )}
      {accept.error ? <ErrorBox error={accept.error} /> : null}
    </div>
  )
}
